// ViewSubscriptions.js (Alternative with local state)
import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  listSubscriptions,
  getSubscriptionFdData,
} from 'api/fieldDelineation/subscriptions';
import { dataLayerStyles, apiLayer } from 'constants/fdstyleData';
import { Box, Typography, Button } from '@mui/material';
import NavBar from 'components/NavBar';
import { Link, useHistory } from 'react-router-dom';
import * as turf from '@turf/turf';
import SubscriptionTable from 'components/Subscriptions/SubscriptionTable';
import SidePanelMenu from 'components/SidePanelMenu';

import styles from './mapview.module.css';

const ViewFDSubscriptions = () => {
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(15);
  const history = useHistory();

  const columns = useMemo(
    () => [
      {
        Header: 'Name',
        accessor: 'name',
        Cell: ({ row }) => {
          const bbox = row?.original?.bbox
            ? turf.bbox(row?.original?.bbox)
            : [];
          const subscriptionName = row?.original?.name ?? 'No Name';
          const encodedName = encodeURIComponent(subscriptionName);
          return (
            <Link
              to={`/delineated-fields/subscriptions/${
                row.original.id
              }?name=${encodedName}&bbox=${bbox.join(',')}`}
            >
              {subscriptionName}
            </Link>
          );
        },
      },
      {
        Header: 'Area (ha)',
        accessor: 'areas',
        Cell: ({ row }) => {
          const areas = row?.original?.props?.areas;
          if (Array.isArray(areas) && areas.length > 0) {
            const totalArea = areas.reduce((acc, cur) => acc + cur, 0);
            return <span>{totalArea.toFixed(2)}</span>;
          }
          return 'N/A';
        },
      },
      {
        Header: 'API Type',
        accessor: 'apitype',
        Cell: ({ row }) => {
          const apilayerRow = row?.original?.props.apiType ?? '';
          const apiLayerStyle = apiLayer[apilayerRow] || {};
          return (
            <Box
              sx={{
                backgroundColor: apiLayerStyle.backgroundColor,
                color: apiLayerStyle.color,
                fontSize: '12px',
                fontWeight: '700',
                display: 'inline-block',
                minWidth: '120px',
              }}
            >
              {apiLayerStyle.text || apilayerRow}
            </Box>
          );
        },
      },
      {
        Header: 'Data Layers',
        accessor: 'dataLayerFdType',
        Cell: ({ row }) => {
          const dataLayerType = row?.original?.props.dataLayerFdType ?? '';
          const style = dataLayerStyles[dataLayerType] || {};
          return (
            <Box
              sx={{
                backgroundColor: style.backgroundColor,
                color: style.color,
                fontSize: style.fontSize,
                padding: style.padding,
                fontWeight: style.fontWeight || '700',
                borderRadius: style.borderRadius || '12px',
                border: style.border,
                display: 'inline-block',
                textAlign: 'center',
                minWidth: '120px',
              }}
            >
              {style.text || dataLayerType}
            </Box>
          );
        },
      },
      {
        Header: 'Status',
        accessor: 'status',
        Cell: ({ row }) => row?.original?.status ?? '',
      },
      {
        Header: 'Created At',
        accessor: 'createdAt',
        Cell: ({ row }) =>
          row?.original?.createdAt
            ? new Date(row?.original?.createdAt).toLocaleString()
            : '',
      },
    ],
    []
  );

  const { isLoading, error, data } = useQuery(
    ['FieldDelineationSubscriptions', pageIndex, pageSize],
    async () => {
      const subscriptionList = await listSubscriptions({
        page: pageIndex,
        limit: pageSize,
      });

      if (Array.isArray(subscriptionList)) {
        return subscriptionList;
      }
      return [];
    },
    {
      refetchInterval: 5000,
    }
  );

  return (
    <div>
      <NavBar Title="FD & Add-on Subscriptions" />
      <div className={styles.dashboardRoot}>
        <SidePanelMenu />
        <div className={styles.viewSubscriptions}>
          <div className={styles.pdrListTitle}>
            <Typography variant="h5" fontWeight={700}>
              My Orders
            </Typography>
            <Button
              variant="contained"
              onClick={() => {
                history.push('/delineated-fields/subscriptions/create');
              }}
            >
              Create New Order
            </Button>
          </div>

          <SubscriptionTable
            columns={columns}
            data={data}
            error={error}
            isLoading={isLoading}
            setQueryPageIndex={setPageIndex}
            setQueryPageSize={setPageSize}
            queryPageIndex={pageIndex}
            queryPageSize={pageSize}
          />
        </div>
      </div>
    </div>
  );
};

export default ViewFDSubscriptions;
